"use client";

import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";

type QuickListDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function QuickListDialog({ open, onOpenChange }: QuickListDialogProps) {
  const [items, setItems] = useState("");
  const router = useRouter();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleSearch = useCallback(() => {
    const parsed = items
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (parsed.length === 0) return;

    const params = new URLSearchParams();
    parsed.forEach((item) => params.append("items", item));
    onOpenChange(false);
    setItems("");
    router.push(`/lista?${params.toString()}`);
  }, [items, router, onOpenChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSearch();
    }
  };

  const content = (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium">Tus productos</label>
      <Textarea
        placeholder={"Ejemplo:\nLeche\nHuevos\nPan\nAguacate"}
        value={items}
        onChange={(e) => setItems(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={8}
        className="flex-1"
        autoFocus
      />
      <p className="text-xs text-muted-foreground">
        Escribe o pega cada producto línea por línea. Buscaremos la mejor coincidencia para cada uno.
      </p>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-2xl">Lista rápida</DialogTitle>
            <DialogDescription>
              La manera más rápida de hacer tu súper. Escribe tu lista y haz tu pedido en segundos.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1">
            {content}
          </div>
          <div className="flex-shrink-0 pt-4">
            <Button
              onClick={handleSearch}
              disabled={items.trim().length === 0}
              className="w-full cursor-pointer"
            >
              Buscar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="text-2xl">Compra tu lista</DrawerTitle>
          <DrawerDescription>
            Salta el scroll infinito. Ve solo los productos de tu lista.
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 flex-1 overflow-y-auto">
          {content}
        </div>
        <DrawerFooter className="pb-8">
          <Button
            onClick={handleSearch}
            disabled={items.trim().length === 0}
            className="w-full cursor-pointer"
          >
            Buscar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
